import { countries } from "@redotech/locale/countries";
import { shopifyProvinces } from "@redotech/locale/provinces";
import { RedoMerchantRpcClient } from "@redotech/merchant-sdk/rpc/client";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import FilterIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import XIcon from "@redotech/redo-icons/x_filled.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoOutboundLabelsRpcClientContext } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { omsConditionStepSchema } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  BaseWhereStep,
  getConditionTypeLabel,
  omsConditionToWhereConditionTypes,
  OmsConditionType,
  OmsCustomString1ConditionStep,
  OmsDeliveryMethodConditionStep,
  OmsLineItemCustomAttributeConditionStep,
  OmsMarketplaceConditionStep,
  OmsOrderNotesConditionStep,
  OmsProductWhereCondition,
} from "@redotech/redo-model/advanced-flow/oms/conditions";
import { OmsConditionStep } from "@redotech/redo-model/advanced-flow/steps";
import { Schema } from "@redotech/redo-model/advanced-flow/type-system/schema";
import {
  RedoShopifyDeliveryMethodTypeToString as RedoShopifyDeliveryMethodLabels,
  RedoShopifyDeliveryMethodType,
} from "@redotech/redo-model/fulfillments/fulfillment-delivery-method-type";
import {
  riskLabels,
  ShopifyRiskAssessmentResult,
} from "@redotech/redo-model/fulfillments/shopify-fulfillment-schemas";
import { orderProviderToLabel, Provider } from "@redotech/redo-model/order";
import { FulfillmentGroupListMatchValuesFilterName } from "@redotech/redo-model/outbound-labels/fulfillment-group-summary-input";
import { TagKind } from "@redotech/redo-model/tag";
import { Team } from "@redotech/redo-model/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import * as inspectorStyles from "@redotech/redo-web/inspector.module.css";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import Fuse from "fuse.js";
import * as React from "react";
import { Fragment, memo, useContext, useMemo, useState } from "react";
import { DeepPartial, FrontendStepType, PartialWithRequiredType } from "..";
import { getPrecedingText } from "../../nodes/oms/oms-condition-node";
import { OmsDateMatchTypeSelect } from "./date-match-type-select";
import { OmsDatePickerInput } from "./date-picker-input";
import { ListMatchFixedValuesInput } from "./list-match-fixed-values-input";
import { OmsNumberMatchTypeSelect } from "./number-match-type-select";
import { OmsListMatchValuesInput } from "./oms-list-match-values-input";
import { OmsListToListMatchTypeSelect } from "./oms-list-to-list-match-type-select";
import { OmsRequireExactMatchInput } from "./oms-require-exact-match-input";
import { OmsSingleNumberStepInput } from "./single-number-step-input";
import { StringMatchTypeSelect } from "./string-match-type-select";
import { StringToListMatchTypeSelect } from "./string-to-list-match-type-select";
import { TextInput } from "./text-input";
import { OmsWeightStepInput } from "./weight-step-input";

type CustomStringConditionType =
  | OmsConditionType.CUSTOM_STRING_1
  | OmsConditionType.CUSTOM_STRING_2
  | OmsConditionType.CUSTOM_STRING_3
  | OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1
  | OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2
  | OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3;

function createOptionsFetcher(
  filterName: FulfillmentGroupListMatchValuesFilterName,
  rpcClient: RedoOutboundLabelsRpcClient,
): ({ searchText }: { searchText: string }) => Promise<string[]> {
  return async ({ searchText }) => {
    const { values } = await rpcClient.getFulfillmentGroupUniqueValues({
      advancedFiltersData: [],
      filterName,
      filterSearch: searchText,
    });
    return values;
  };
}

// search-tag:ADDING_AN_OMS_CONDITION
const OmsConditionInputs = memo(function OmsConditionInputs({
  schema,
  step,
  setStep,
  onClose,
}: {
  step: PartialWithRequiredType<OmsConditionStep>;
  setStep: (step: PartialWithRequiredType<OmsConditionStep>) => void;
  schema: Schema;
  onClose?: () => void;
}) {
  const setConditionType = useHandler(
    (item: RedoListItem<OmsConditionType>) => {
      setStep({
        type: step.type,
        nextTrueId: step.nextTrueId,
        nextFalseId: step.nextFalseId,
        id: step.id,
        conditionType: item.value,
      } as PartialWithRequiredType<OmsConditionStep>);
    },
  );

  const setStepFields = useHandler((newStep: DeepPartial<OmsConditionStep>) => {
    setStep({
      ...newStep,
      type: step.type,
      nextTrueId: step.nextTrueId,
      nextFalseId: step.nextFalseId,
      id: step.id,
    });
  });

  const showFilterButton = useMemo(() => {
    if (!step.conditionType) {
      return false;
    }
    return omsConditionToWhereConditionTypes[step.conditionType].length > 0;
  }, [step]);

  const onClickFilter = useHandler(() => {
    const where = step.where ?? [];
    where.push({});
    setStepFields({
      ...step,
      where: where as DeepPartial<OmsProductWhereCondition>[],
    });
  });

  const precedingText = getPrecedingText(step);

  return (
    <Flex dir="column" gap="xl" justify="flex-start">
      <Flex justify="space-between" w="full">
        <Text fontSize="sm" fontWeight="semibold">
          Condition
        </Text>
        {onClose && (
          <div className={inspectorStyles.closeButton} onClick={onClose}>
            <XIcon height="16" width="16" />
          </div>
        )}
      </Flex>
      <Flex dir="column" gap="none">
        <Flex align="center" justify="space-between" w="full">
          <Text fontSize="xs" fontWeight="medium">
            If {precedingText}
          </Text>
          {showFilterButton && (
            <RedoButton
              IconLeading={() => <FilterIcon />}
              onClick={onClickFilter}
            />
          )}
        </Flex>
        <Flex align="center" dir="row" gap="md" w="full" wrap="wrap">
          <ConditionFields
            setConditionType={setConditionType}
            setStep={setStepFields}
            step={step}
          />
        </Flex>
      </Flex>
      <WhereFilters
        clauses={step.where ?? []}
        setStep={setStepFields}
        step={step}
      />
    </Flex>
  );
});

const ConditionFields = memo(function ConditionFields({
  conditionTypeOptions,
  step,
  setStep,
  setConditionType,
  index = 0,
}: {
  conditionTypeOptions?: OmsConditionType[];
  step: DeepPartial<OmsConditionStep>;
  setStep: (step: DeepPartial<OmsConditionStep>, index: number) => void;
  setConditionType: (
    item: RedoListItem<OmsConditionType>,
    index: number,
  ) => void;
  index?: number;
}) {
  const team = useContext(TeamContext);
  const outboundLabelRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );

  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const [searchString, setSearchString] = useState("");

  const setStepFields = useHandler((newStep: DeepPartial<OmsConditionStep>) => {
    setStep(newStep, index);
  });

  const { conditionFields } = getConditionFields(
    step,
    setStepFields,
    outboundLabelRpcClient,
    merchantRpcClient,
    team,
  );

  const optionSelected = useHandler(
    ({ item }: { item: RedoListItem<OmsConditionType> }) => {
      setConditionType(item, index);
    },
  );

  const allOptions = useMemo(
    () => getOmsConditionTypeOptions(conditionTypeOptions, team),
    [conditionTypeOptions, team],
  );

  const conditionSearcher = useMemo(() => {
    return new Fuse<RedoListItem<OmsConditionType>>([], {
      keys: ["text"],
      threshold: 0.3,
    });
  }, []);

  const filteredOptions = useSearch(
    conditionSearcher,
    allOptions,
    searchString,
  );

  return (
    <>
      <RedoSingleSelectDropdownInput<OmsConditionType>
        fitToAnchor={false}
        flexGrow={false}
        options={filteredOptions}
        optionSelected={optionSelected}
        placeholder="Select a field"
        searchPlaceholder="Search conditions..."
        searchString={searchString}
        selectedItem={allOptions.find((o) => o.id === step.conditionType)}
        setSearchString={setSearchString}
        size="xs"
      />
      {conditionFields.map((field, index) => (
        <Fragment key={index}>{field}</Fragment>
      ))}
    </>
  );
});

const WhereFilters = memo(function WhereFilters({
  clauses,
  step,
  setStep,
}: {
  clauses: DeepPartial<BaseWhereStep>[];
  step: PartialWithRequiredType<OmsConditionStep>;
  setStep: (step: PartialWithRequiredType<OmsConditionStep>) => void;
}) {
  const setSubStep = useHandler(
    (newStep: DeepPartial<OmsConditionStep>, index: number) => {
      if (step.where === undefined) {
        step.where = [];
      }
      if (index > step.where.length) {
        index = step.where.length;
      }
      step.where[index] = newStep;
      setStep(step);
    },
  );

  const setConditionType = useHandler(
    (item: RedoListItem<OmsConditionType>, index: number) => {
      setSubStep(
        { conditionType: item.value } as DeepPartial<OmsConditionStep>,
        index,
      );
    },
  );

  const onDeleteWhereClause = useHandler((index: number) => {
    step.where?.splice(index, 1);
    setStep(step);
  });

  return (
    <Flex dir="column" justify="flex-start">
      {clauses.map((clause, index) => (
        <Flex
          dir="column"
          gap="none"
          justify="flex-start"
          key={`where-${index}`}
        >
          <Flex align="center" justify="space-between" w="full">
            <Text fontSize="xs" fontWeight="medium">
              {index === 0 ? "Where" : "And"}
            </Text>
            <RedoButton
              IconLeading={() => <XIcon />}
              onClick={() => onDeleteWhereClause(index)}
            />
          </Flex>
          <Flex align="center" dir="row" gap="md" w="full" wrap="wrap">
            <ConditionFields
              conditionTypeOptions={
                step.conditionType
                  ? omsConditionToWhereConditionTypes[step.conditionType]
                  : undefined
              }
              index={index}
              setConditionType={setConditionType}
              setStep={setSubStep}
              step={clause as DeepPartial<OmsConditionStep>}
            />
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
});

function getConditionFields(
  step: DeepPartial<OmsConditionStep>,
  setStep: (step: DeepPartial<OmsConditionStep>) => void,
  outboundLabelRpcClient: RedoOutboundLabelsRpcClient,
  merchantRpcClient: RedoMerchantRpcClient,
  team?: Team,
) {
  let conditionFields: React.ReactNode[] = [];
  let showFilterButton = false;

  switch (step.conditionType) {
    case OmsConditionType.FRAUD_RISK:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural={false}
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="fraud-risk-matcher"
          optionsFetcher={() =>
            Promise.resolve(
              Object.values(ShopifyRiskAssessmentResult).map(
                (value) => riskLabels[value],
              ),
            )
          }
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Select risk types..."
        />,
      ];
      break;
    case OmsConditionType.AVAILABLE_INVENTORY:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer
            money={false}
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.ORDER_TAG:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="order-tags-matcher"
          optionsFetcher={createOptionsFetcher(
            "shopifyOrderTags",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search tags..."
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.SELECTED_SHIPPING:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural={false}
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="shipping-matcher"
          optionsFetcher={createOptionsFetcher(
            "requestedService",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search shipping lines..."
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.SKU:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="sku-matcher"
          optionsFetcher={createOptionsFetcher(
            "itemSkus",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search product SKUs..."
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.PRODUCT_NAME:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="product-name-matcher"
          optionsFetcher={createOptionsFetcher(
            "productTitle",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search product names..."
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      showFilterButton = true;
      break;
    case OmsConditionType.VARIANT_TITLE:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="variant-title-matcher"
          optionsFetcher={createOptionsFetcher(
            "productVariant",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search variant titles..."
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      showFilterButton = true;
      break;
    case OmsConditionType.PRODUCT_TYPE:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="product-type-matcher"
          optionsFetcher={createOptionsFetcher(
            "productType",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search product types..."
        />,
        <OmsRequireExactMatchInput key="3" setStep={setStep} step={step} />,
      ];
      showFilterButton = true;
      break;
    case OmsConditionType.ITEM_QUANTITY:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer
            money={false}
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.PACKAGE_WEIGHT_OZ:
      conditionFields = [
        <OmsNumberMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsWeightStepInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.TOTAL_VALUE:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer={false}
            money
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.TAXES_PAID:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer={false}
            money
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.DESTINATION_COUNTRY:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="country-name-matcher"
          optionsFetcher={() =>
            Promise.resolve(countries.map((country) => country.name))
          }
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search countries..."
        />,
      ];
      break;
    case OmsConditionType.DESTINATION_PROVINCE:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="province-name-matcher"
          optionsFetcher={({ searchText }) =>
            Promise.resolve(
              shopifyProvinces
                .filter((province) =>
                  province.province
                    .toLowerCase()
                    .includes(searchText.toLowerCase()),
                )
                .sort((p1, p2) => p1.province.localeCompare(p2.province))
                .map((province) => province.province)
                .slice(0, 100),
            )
          }
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search provinces..."
        />,
      ];
      break;
    case OmsConditionType.DELIVERY_METHOD:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <ListMatchFixedValuesInput
          key="delivery-method-matcher"
          label="Select delivery method(s)"
          options={Object.entries(RedoShopifyDeliveryMethodType).map(
            ([id, value]) => ({
              id,
              value,
              label: RedoShopifyDeliveryMethodLabels[value],
            }),
          )}
          setStep={setStep}
          step={step as PartialWithRequiredType<OmsDeliveryMethodConditionStep>}
        />,
      ];
      break;
    case OmsConditionType.STREET_ADDRESS:
      // Street address uses a different input UI with StringMatchOperator
      conditionFields = [
        <StringMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <TextInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.STREET_ADDRESS_LIST_INPUT:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="street-address-list-input-matcher"
          optionsFetcher={() => {
            return Promise.resolve([]);
          }}
          setStep={setStep}
          step={step}
          tagInputPlaceholder=""
        />,
      ];
      break;
    case OmsConditionType.SALES_CHANNEL:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="sales-channel-matcher"
          optionsFetcher={createOptionsFetcher(
            "stores",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search sales channels..."
        />,
      ];
      break;
    case OmsConditionType.PRODUCT_TAG:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="product-tag-matcher"
          optionsFetcher={createOptionsFetcher(
            "shopifyProductTags",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search product tags..."
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.FULFILLMENT_LOCATION:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="location-matcher"
          optionsFetcher={async () => {
            try {
              // Include locations from teams this 3PL fulfills for
              const { locations } =
                await merchantRpcClient.getLocationsWithFulfillsFor({});
              return locations.map((location) => location.name);
            } catch (error) {
              console.error(error);
              return [];
            }
          }}
          preserveCommas
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search locations..."
        />,
      ];
      break;
    case OmsConditionType.ORIGIN_STORE:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="origin-store-matcher"
          optionsFetcher={createOptionsFetcher(
            "originStore",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search origin stores..."
        />,
      ];
      break;
    case OmsConditionType.ORDER_NOTES:
      conditionFields = [
        <StringMatchTypeSelect
          key="1"
          setStep={setStep}
          step={step as PartialWithRequiredType<OmsOrderNotesConditionStep>}
        />,
        <TextInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.MARKETPLACE:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <ListMatchFixedValuesInput
          key="marketplace-matcher"
          label="Select marketplace(s)"
          options={Object.values(Provider).map((value) => ({
            id: value,
            value,
            label: orderProviderToLabel[value],
          }))}
          setStep={setStep}
          step={step as PartialWithRequiredType<OmsMarketplaceConditionStep>}
        />,
      ];
      break;
    case OmsConditionType.SHIPMENT_TAG:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="shipment-tag-matcher"
          optionsFetcher={async () => {
            try {
              const { tags } = await merchantRpcClient.getTeamTags({
                kind: TagKind.FULFILLMENT_GROUP,
              });
              return tags.map((tag) => tag.name);
            } catch (error) {
              console.error(error);
              return [];
            }
          }}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search shipment tags..."
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.CUSTOM_STRING_1:
    case OmsConditionType.CUSTOM_STRING_2:
    case OmsConditionType.CUSTOM_STRING_3:
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1:
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2:
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3: {
      const conditionTypeToFilterName: Record<
        CustomStringConditionType,
        FulfillmentGroupListMatchValuesFilterName
      > = {
        [OmsConditionType.CUSTOM_STRING_1]: "customString1",
        [OmsConditionType.CUSTOM_STRING_2]: "customString2",
        [OmsConditionType.CUSTOM_STRING_3]: "customString3",
        [OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1]: "customAttributeString1",
        [OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2]: "customAttributeString2",
        [OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3]: "customAttributeString3",
      };

      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          addTagText="Add value"
          key={`custom-string-${step.conditionType}-matcher`}
          optionsFetcher={createOptionsFetcher(
            conditionTypeToFilterName[step.conditionType!],
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step as PartialWithRequiredType<OmsCustomString1ConditionStep>}
          tagInputPlaceholder={`Select ${getConditionTypeLabel(
            step.conditionType,
            team,
          )} values...`}
        />,
      ];
      break;
    }
    case OmsConditionType.CUSTOM_NUMBER_1:
    case OmsConditionType.CUSTOM_NUMBER_2:
    case OmsConditionType.CUSTOM_NUMBER_3:
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_1:
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_2:
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_3:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer={false}
            money={false}
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.CUSTOM_DATE_1:
    case OmsConditionType.CUSTOM_DATE_2:
    case OmsConditionType.CUSTOM_DATE_3:
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_1:
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_2:
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_3:
      conditionFields = [
        <OmsDateMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsDatePickerInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.CUSTOMER_NAME:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="customer-name-matcher"
          optionsFetcher={createOptionsFetcher(
            "customer",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search customer names..."
        />,
      ];
      break;
    case OmsConditionType.CUSTOMER_ORDER_COUNT:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer
            money={false}
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.CUSTOMER_TAG:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="customer-tag-matcher"
          optionsFetcher={createOptionsFetcher(
            "customerTags",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search tags..."
        />,
        <OmsRequireExactMatchInput key="2" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.ITEM_COST:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer={false}
            money
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.VENDOR:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="vendor-matcher"
          optionsFetcher={createOptionsFetcher(
            "vendors",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search vendors..."
        />,
        <OmsRequireExactMatchInput key="2" setStep={setStep} step={step} />,
      ];
      showFilterButton = true;
      break;
    case OmsConditionType.USPS_ZONE:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="usps-zone-matcher"
          optionsFetcher={() =>
            Promise.resolve(
              [
                { label: "Zone 1", value: "1" },
                { label: "Zone 2", value: "2" },
                { label: "Zone 3", value: "3" },
                { label: "Zone 4", value: "4" },
                { label: "Zone 5", value: "5" },
                { label: "Zone 6", value: "6" },
                { label: "Zone 7", value: "7" },
                { label: "Zone 8", value: "8" },
              ].map((zone) => zone.value),
            )
          }
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Select zones..."
        />,
      ];
      break;
    case OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE:
      conditionFields = [
        <RedoTextInput
          key="attribute-key-input"
          placeholder="Attribute key"
          setValue={(value) => setStep({ ...step, matchKey: value })}
          size="sm"
          value={
            (step as DeepPartial<OmsLineItemCustomAttributeConditionStep>)
              .matchKey || ""
          }
        />,
        <StringToListMatchTypeSelect key="2" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          addTagText="Add value"
          key="line-item-custom-attribute-matcher"
          optionsFetcher={() => Promise.resolve([])}
          setStep={setStep}
          step={
            step as PartialWithRequiredType<OmsLineItemCustomAttributeConditionStep>
          }
          tagInputPlaceholder="Enter attribute values..."
        />,
      ];
      break;
    case OmsConditionType.ADDRESS_VERIFIED:
      conditionFields = [
        <RedoSingleSelectDropdownInput<boolean>
          key="address-verified-select"
          options={[
            { id: "verified", type: "text", text: "Is verified", value: true },
            {
              id: "not-verified",
              type: "text",
              text: "Is not verified",
              value: false,
            },
          ]}
          optionSelected={({ item }) =>
            setStep({ ...step, matchValue: item.value })
          }
          placeholder="Select..."
          selectedItem={
            step.matchValue === true
              ? {
                  id: "verified",
                  type: "text",
                  text: "Is verified",
                  value: true,
                }
              : step.matchValue === false
                ? {
                    id: "not-verified",
                    type: "text",
                    text: "Is not verified",
                    value: false,
                  }
                : undefined
          }
          size="xs"
        />,
      ];
      break;
    case undefined:
      break;
    default:
      assertNever(step);
  }
  return { conditionFields, showFilterButton };
}

function getOmsConditionTypeOptions(
  conditionTypes?: OmsConditionType[],
  team?: Team,
): TextRedoListItem<OmsConditionType>[] {
  if (!conditionTypes) {
    // get all condition types, but filter out custom field condition types if the merchant doesn't have them configured
    conditionTypes = Object.values(OmsConditionType).filter((type) => {
      return (
        !(
          type == OmsConditionType.CUSTOM_STRING_1 &&
          !team?.settings?.outboundLabels?.customMetadata?.customString1
        ) &&
        !(
          type == OmsConditionType.CUSTOM_STRING_2 &&
          !team?.settings?.outboundLabels?.customMetadata?.customString2
        ) &&
        !(
          type == OmsConditionType.CUSTOM_STRING_3 &&
          !team?.settings?.outboundLabels?.customMetadata?.customString3
        ) &&
        !(
          type == OmsConditionType.CUSTOM_DATE_1 &&
          !team?.settings?.outboundLabels?.customMetadata?.customDate1
        ) &&
        !(
          type == OmsConditionType.CUSTOM_DATE_2 &&
          !team?.settings?.outboundLabels?.customMetadata?.customDate2
        ) &&
        !(
          type == OmsConditionType.CUSTOM_DATE_3 &&
          !team?.settings?.outboundLabels?.customMetadata?.customDate3
        ) &&
        !(
          type == OmsConditionType.CUSTOM_NUMBER_1 &&
          !team?.settings?.outboundLabels?.customMetadata?.customNumber1
        ) &&
        !(
          type == OmsConditionType.CUSTOM_NUMBER_2 &&
          !team?.settings?.outboundLabels?.customMetadata?.customNumber2
        ) &&
        !(
          type == OmsConditionType.CUSTOM_NUMBER_3 &&
          !team?.settings?.outboundLabels?.customMetadata?.customNumber3
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeString1
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeString2
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeString3
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_1 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeNumber1
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_2 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeNumber2
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_3 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeNumber3
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_DATE_1 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeDate1
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_DATE_2 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeDate2
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_DATE_3 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeDate3
        )
      );
    });
  }
  const conditionTypeOptions: TextRedoListItem<OmsConditionType>[] =
    conditionTypes.map((type) => ({
      value: type,
      type: "text",
      id: type,
      text: getConditionTypeLabel(type, team),
    }));
  conditionTypeOptions.sort((a, b) => a.text.localeCompare(b.text));
  return conditionTypeOptions;
}

export const omsConditionStepType: FrontendStepType<
  PartialWithRequiredType<OmsConditionStep>
> = {
  valid: (step) => omsConditionStepSchema.safeParse(step).success,
  type: "custom",
  customInspector: OmsConditionInputs,
};
